import React, { useState } from "react";
import {
  Grid,
  Card,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Box,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import MainCard from "ui-component/cards/MainCard";
import CouponApi from "apis/coupon.api";
import { useEffect } from "react";
import toast from "react-hot-toast";

const CouponsDiscount = () => {

  const couponApi = new CouponApi();

  const [coupons, setCoupons] = useState([]);

  const [openModal, setOpenModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const [selectedCoupon, setSelectedCoupon] = useState(null);

  const [formData, setFormData] = useState({
    code: "",
    discountType: "PERCENTAGE",
    discountValue: "",
    status: "ACTIVE",
  });

  const fetchCoupons = async () => {
    try {
      const res = await couponApi.getAllCoupons();

      if (res?.data?.status === "success") {
        setCoupons(res.data.data || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const handleOpenCreate = () => {
    setIsEdit(false);

    setFormData({
      code: "",
      discountType: "PERCENTAGE",
      discountValue: "",
      status: "ACTIVE",
    });

    setOpenModal(true);
  };

  const handleEdit = (coupon) => {
    setSelectedCoupon(coupon);
    setIsEdit(true);

    setFormData({
      code: coupon.code,
      discountType: coupon.discountType,
      discountValue: coupon.discountValue,
      status: coupon.status,
    });

    setOpenModal(true);
  };

  const handleDelete = async (couponId) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this coupon?"
    );

    if (!confirmed) return;

    try {
      const res = await couponApi.deleteCoupon(couponId);

      if (res?.data?.status === "success") {
        toast.success("Coupon deleted successfully");
        await fetchCoupons();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async () => {
    try {
      if (!formData.code || !formData.discountValue) {
        return;
      }

      let res;

      if (isEdit) {
        res = await couponApi.updateCoupon(
          selectedCoupon._id,
          formData
        );
      } else {
        res = await couponApi.createCoupon(formData);
      }

      if (res?.data?.status === "success") {
        setOpenModal(false);
        toast.success(
          isEdit
            ? "Coupon updated successfully"
            : "Coupon created successfully"
        );

        await fetchCoupons();
      }
    } catch (err) {
      console.error(err);
    }
  };


  return (
    <MainCard
      title="Coupons & Discounts"
      secondary={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
        >
          Create Coupon
        </Button>
      }
    >
      <Card>
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>S.No</TableCell>
                  <TableCell>Coupon Code</TableCell>
                  <TableCell>Discount Type</TableCell>
                  <TableCell>Discount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {coupons.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No Coupons Found
                    </TableCell>
                  </TableRow>
                ) : (
                  coupons.map((coupon, index) => (
                    <TableRow key={coupon._id}>
                      <TableCell>{index + 1}</TableCell>

                      <TableCell>
                        <Typography fontWeight={600}>
                          {coupon.code}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={
                            coupon.discountType === "PERCENTAGE"
                              ? "Percentage"
                              : "Fixed Amount"
                          }
                          color="primary"
                          size="small"
                        />
                      </TableCell>

                      <TableCell>
                        {coupon.discountType === "PERCENTAGE"
                          ? `${coupon.discountValue}%`
                          : `₹${coupon.discountValue}`}
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={coupon.status}
                          color={
                            coupon.status === "ACTIVE"
                              ? "success"
                              : "default"
                          }
                          size="small"
                        />
                      </TableCell>

                      <TableCell align="center">
                        <Box
                          display="flex"
                          justifyContent="center"
                          gap={1}
                        >
                          <IconButton
                            color="primary"
                            onClick={() => handleEdit(coupon)}
                          >
                            <EditIcon />
                          </IconButton>

                          <IconButton
                            color="error"
                            onClick={() => handleDelete(coupon._id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Card>

      {/* Create / Edit Modal */}

      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {isEdit ? "Edit Coupon" : "Create Coupon"}
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Coupon Code"
                value={formData.code}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    code: e.target.value.toUpperCase(),
                  })
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Discount Type"
                value={formData.discountType}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    discountType: e.target.value,
                  })
                }
              >
                <MenuItem value="PERCENTAGE">
                  Percentage (%)
                </MenuItem>

                <MenuItem value="FIXED">
                  Fixed Amount (₹)
                </MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label={
                  formData.discountType === "PERCENTAGE"
                    ? "Discount Percentage"
                    : "Discount Amount"
                }
                value={formData.discountValue}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    discountValue: e.target.value,
                  })
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Status"
                value={formData.status}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    status: e.target.value,
                  })
                }
              >
                <MenuItem value="ACTIVE">
                  Active
                </MenuItem>

                <MenuItem value="INACTIVE">
                  Inactive
                </MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOpenModal(false)}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
          >
            {isEdit ? "Update Coupon" : "Create Coupon"}
          </Button>
        </DialogActions>
      </Dialog>
    </MainCard>
  );
};

export default CouponsDiscount;